import { type } from "arktype";
import type stats from "~/data/stats";
import type treeUpgrades from "~/data/tree_upgrades";
import type upgrades from "~/data/upgrades";
import type { ClickEffectType } from "~/js/click";

export type StatName =
	| (typeof stats)[number]["stat"]
	| "earned_xp"
	| "opened_rarities_0"
	| "opened_rarities_1"
	| "opened_rarities_7"
	| "earned_cash"
	| "skin_sold_r0"
	| "skin_sold_r1"
	| "skin_sold_r2"
	| "skin_sold_r3"
	| "skin_sold_r4"
	| "skin_sold_r5"
	| "skin_sold_r6"
	| "skin_sold_r7"
	| "spend_chips"
	| "win_chips"
	| "crash_bet_100x"
	| "crash_low_bet_100x"
	| `cashout_${number}x`
	| "lucky_wheel_cash_winnings"
	| "click_effects_decayed"
	| `click_effects_decayed_${ClickEffectType}`
	| "click_effects_summoned"
	| `click_effects_summoned_${ClickEffectType}`
	| "click_effects_activated"
	| `click_effects_activated_${ClickEffectType}`
	| "total_bets"
	| "total_bets_won"
	| "total_bets_lost"
	| "total_coinflips_losses"
	| "total_coinflips_won_bet100"
	| "total_coinflips_won_bet1000"
	| "total_coinflips_won_bet10000"
	| "total_coinflips_lost_bet100"
	| "total_coinflips_lost_bet1000"
	| "total_coinflips_lost_bet10000"
	| "total_mines_games"
	| "total_mines_won"
	| "total_mines_lost"
	| "total_mines_won_mo20"
	| "roulette_wins"
	| "roulette_winnings"
	| "roulette_wins_2to1"
	| "roulette_wins_35to1"
	| "spend_chips_on_roulette"
	| "win_chips_on_slotmachine"
	| "spend_chips_on_slotmachine"
	| "win_100x_on_slotmachine"
	| "win_special_on_slotmachine"
	| "creation"
	| "win_jackpot_low"
	| "win_jackpot_big"
	| "lose_jackpot_close"
	| "lose_jackpot_big"
	| "win_dice_throw_on_slotmachine"
	| "get_5_in_a_row_on_slotmachine";

export type LuckyWheelWins = (StatReward | ItemReward) & {
	date: number;
	color: string;
};

export type ItemName = (typeof Item.infer)["name"];
const WeaponUnion = type.or(
	"'AK-47'",
	"'AUG'",
	"'AWP'",
	"'Bayonet'",
	"'Bloodhound Gloves'",
	"'Bowie Knife'",
	"'Broken Fang Gloves'",
	"'Butterfly Knife'",
	"'CZ75-Auto'",
	"'Classic Knife'",
	"'Desert Eagle'",
	"'Driver Gloves'",
	"'Dual Berettas'",
	"'FAMAS'",
	"'Falchion Knife'",
	"'Five-SeveN'",
	"'Flip Knife'",
	"'G3SG1'",
	"'Galil AR'",
	"'Glock-18'",
	"'Gut Knife'",
	"'Hand Wraps'",
	"'Huntsman Knife'",
	"'Hydra Gloves'",
	"'Karambit'",
	"'Kukri Knife'",
	"'M249'",
	"'M4A1-S'",
	"'M4A4'",
	"'M9 Bayonet'",
	"'MAC-10'",
	"'MAG-7'",
	"'MP5-SD'",
	"'MP7'",
	"'MP9'",
	"'Moto Gloves'",
	"'Navaja Knife'",
	"'Negev'",
	"'Nomad Knife'",
	"'Nova'",
	"'P2000'",
	"'P250'",
	"'P90'",
	"'PP-Bizon'",
	"'Paracord Knife'",
	"'R8 Revolver'",
	"'SCAR-20'",
	"'SG 553'",
	"'SSG 08'",
	"'Sawed-Off'",
	"'Shadow Daggers'",
	"'Skeleton Knife'",
	"'Specialist Gloves'",
	"'Sport Gloves'",
	"'Stiletto Knife'",
	"'Survival Knife'",
	"'Talon Knife'",
	"'Tec-9'",
	"'UMP-45'",
	"'USP-S'",
	"'Ursus Knife'",
	"'XM1014'",
	"'Zeus x27'",
);

const Sticker = type({
	name: "string",
	positionX: "string",
	positionY: "string",
	wearRating: "0<= number < 1",
});

const Charm = type({
	name: "string",
	positionX: "string",
	positionY: "string",
	patternNumber: "1 <= number <= 100000000",
});

// currently not used, will be used on inventory revamp
export const NewWeaponSkin = type({
	weapon: WeaponUnion,
	finishCatalog: "number.integer >= 0",
	patternTemplate: "0 <=number.integer <= 100",
	wearRating: "0<= number < 1",
	stickers: Sticker.array().atMostLength(5),
	charm: Charm,
	nameTag: "string",
	stattrak: "boolean",
	souvenir: "boolean",
});

type Brand<K, T> = K & { __brand: T };

export type UpgradeRequirement =
	| {
			type: "money";
			total: number;
			isPrice: boolean;
	  }
	| {
			type: "level";
			min?: number;
			max?: number;
			equals?: number;
			lt?: number;
			gt?: number;
	  }
	| {
			type: "stat";
			stat: StatName;
			min?: number;
			max?: number;
			equals?: number;
			lt?: number;
			gt?: number;
	  };

export type UpgradeName = keyof typeof upgrades;
export type TreeUpgradeKey = keyof typeof treeUpgrades;
export type TreeUpgrade = {
	name: string;
	parents: (string | null)[];
	description: string;
	position: [number, number];
	requirements: UpgradeRequirement[];
	rewards: [Reward, ...Reward[]];
	icon: string; // relative url
};

type InterpolatableString = Brand<string, "interpolatableString">;
export type Upgrade = {
	name: string;
	desc: string;
	default: number;
	displayValue: InterpolatableString;
};

export type StatReward = {
	// for achievements
	type: "xp" | "money";
	amount: number;
	color?: string; // hex color code
};

export type ItemReward = {
	type: "item";
	amount: number;
	item: string;
	display: string[];
	color: string;
	text: string;
};
type UpgradeReward = {
	type: "upgrade";
	upgrade: UpgradeName;
} & (
	| {
			set: number;
	  }
	| {
			increase: number;
	  }
);

export type Reward = StatReward | ItemReward | UpgradeReward;
export type AchievementRequirement = {
	objective: StatName;
	amount: number;
};

export type AchievementRequirementCalculated = {
	objective: "level";
	amount: number;
	objectiveType: "calculatedUserStat"; // Idk why this can't just be inferered based off the objective key???,
};

export type Achievement = {
	name: string;
	description: string;
	requirements: [
		AchievementRequirement | AchievementRequirementCalculated,
		...(AchievementRequirement | AchievementRequirementCalculated)[],
	];
	rewards: [Reward, ...Reward[]];
};

const StickerTypes = type.or(
	"'Blue Sticker'",
	"'Chicken Sticker'",
	"'CT Sticker'",
	"'Foil Sticker'",
	"'Glitter Sticker'",
	"'Gold Sticker'",
	"'H Sticker'",
	"'Holo Sticker'",
	"'Lenticular Sticker'",
	"'Red Sticker'",
	"'Sticker'",
	"'T Sticker'",
);

const BaseItem = type({
	"+": "reject",
	name: "string#itemName",
	src: "string",
	price: "number.integer >= 0",
	rarity: "0 | 1 | 2 | 3 | 4 | 5 | 6 | 7", // 0=consumer, 1=industrial, 2=milspec, 3=restricted, 4=classified, 5=covert, 6=exceedingly rare, 7=contraband
});
export const ContainerItem = BaseItem.merge({
	// all cases besides collections have keys, no capsules have keys besides the community capsules
	type: "'case'|'capsule'",
	content: {
		items: "string#itemName[]",
	},
	"keyName?": "string#itemName",
});

const KeyItem = BaseItem.merge({
	type: "'key'",
});

const MasterSkinItem = BaseItem.merge({
	"+": "reject",
	type: "'skin'",
	masterItem: "true",

	skin: "string",
	short: "string#itemName",
	weapon: WeaponUnion,

	minPrice: "number.integer >=0",
	stattrakAvailable: "boolean = false",
	souvenirAvailable: "boolean = false",
	qualities: type("1 | 2 | 3 | 4 |5")
		.array()
		.or(["null"])
		.pipe((arr) => {
			if (arr[0] === null) return null;
			return arr as (1 | 2 | 3 | 4 | 5)[];
		}),
});

export type Quality = 1 | 2 | 3 | 4 | 5;
const SkinItem = BaseItem.merge({
	"+": "reject",
	"masterItem?": "false",
	type: "'skin'",
	skin: "string",
	short: "string#itemName",
	weapon: WeaponUnion,

	"weight?": "number",
	souvenir: "boolean = false",
	stattrak: "boolean = false",
	quality: type("1 | 2 | 3 | 4 |5 | null").default(null),
}).pipe((obj) => {
	return obj as Omit<typeof obj, "masterItem">;
});

const StickerItem = BaseItem.merge({
	type: "'sticker'",
	sticker: "string",
	stickerType: StickerTypes,
});

export const Item = type.or(ContainerItem, KeyItem, MasterSkinItem, SkinItem, StickerItem);

export type ContainerItem = typeof ContainerItem.infer;
export type KeyItem = typeof KeyItem.infer;
export type MasterSkinItem = typeof MasterSkinItem.infer;
export type StickerItem = typeof StickerItem.infer;
export type SkinItem = typeof SkinItem.infer;
export type Item = ContainerItem | KeyItem | MasterSkinItem | SkinItem | StickerItem;
export type PagePath = PageId | `${PageId}$${string}`;
export type PageId =
	| "" // idk why it sometimes emits an empty string
	| "about"
	| "achievements"
	| "betting"
	| "casino-withdraw"
	| "click"
	| "coinflip-online-game"
	| "coinflip-online-select"
	| "coinflip-online"
	| "coinflip"
	| "collection"
	| "crash"
	| "inspect"
	| "inventory"
	| "inventory2"
	| "jackpot-online"
	| "jackpot"
	| "mines"
	| "missions"
	| "opencase"
	| "prestige"
	| "roulette"
	| "selectItems"
	| "shooter"
	| "shop"
	| "slots"
	| "spin"
	| "stats"
	| "test"
	| "tradeup"
	| "upgrades"
	| "welcome"
	| "winnings";

type PlayerData = {
	id: number;
	profile: string;
	value: number;
	items: string;
};
export type JackpotGameState = {
	gameId: boolean;
	state: "OPEN" | "CLOSED" | "FINISHED" | "WAITING_FOR_PLAYERS";
	time: number; //unix time in seconds when started
	rollTime: number; //unix time in seconds when rolling starts
	maxBet: number;
	maxItems: number;
	maxPlayers: number;
	winner: number; // -1 for no winner yet
	players: PlayerData[];
};
